import { useMemo, useState } from 'react';
import { CreateResponse } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config_service.pb';
import { useQueryClient } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  FormFieldActionTypes,
  Heading,
  MultiStep,
  MultiStepModalProps,
  PrimaryButton,
  TextField,
  useAlert,
  useForm,
  useMultiStepModalControl,
} from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { trackingId } from '../../../tracking';
import { AudioItem } from '../../audio-picker/types';
import { DEFAULT_STANDARD_MEDIA } from '../../media-picker';
import { AssignDeviceStepCard } from './assign-device';
import { CallerExperienceCard, LocationsCard, RoutingCard } from './call-queue-page';
import { SchemaFormValues, createSubmitPayload, populateSchema } from './schema';
import { CallQueueState, CallQueueRingType } from './types';
import { useCallQueueMutations } from './use-call-queue-mutations';

const getDefaultState = (tenantId: string): CallQueueState => ({
  tenantId: tenantId,
  allowExit: false,
  callQueueId: '',
  deviceRingType: CallQueueRingType.RING_TYPE_RING_ALL,
  devices: [],
  greetingEnabled: false,
  greetingMediaId: '',
  holdMusicId: DEFAULT_STANDARD_MEDIA,
  locationIds: [],
  // Default to 10m when creating
  maxWaitTimeMs: 600000,
  name: '',
  positionAnnouncement: {
    firstAnnouncementDelayMs: 0,
    repeatIntervalMs: 0,
  },
  positionAnnouncementEnabled: false,
});

export const CreateCallQueueButton = ({ tenantLocation }: { tenantLocation: SettingsTenantLocation }) => {
  const { t } = useTranslation('phone');

  const { triggerProps, ...createCallQueueModalProps } = useMultiStepModalControl(
    { onComplete: () => {}, onCancel: () => {}, showCancelAction: true },
    { onClose: () => {} }
  );

  const handleOpenCreateCallQueue = () => {
    createCallQueueModalProps.goToStep({ orIndex: 0 });
    triggerProps.onClick();
  };
  return (
    <>
      <PrimaryButton
        size='large'
        {...triggerProps}
        onClick={handleOpenCreateCallQueue}
        css={{ width: 'fit-content' }}
        trackingId={trackingId({ context: 'setting', feature: 'call-queues', details: 'create' })}
      >
        {t('Create Call Queue')}
      </PrimaryButton>
      <CreateModal tenantLocation={tenantLocation} {...createCallQueueModalProps} />
    </>
  );
};

type CreateCallQueueModalProps = {
  tenantLocation: SettingsTenantLocation;
  onSuccess?: (data: CreateResponse) => void;
} & MultiStepModalProps;

export const CreateModal = ({ tenantLocation, onSuccess, ...createCallQueueModalProps }: CreateCallQueueModalProps) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const onClose = createCallQueueModalProps.modalProps.onClose;
  const [callQueue, setCallQueue] = useState<CallQueueState>(() => getDefaultState(tenantLocation.phoneTenantId ?? ''));
  const queryClient = useQueryClient();
  const { create } = useCallQueueMutations(tenantLocation.phoneTenantId ?? '');
  const { navigate } = useSettingsNavigate();
  const populatedSchema = useMemo(() => {
    return populateSchema(callQueue);
  }, [callQueue]);

  const form = useForm({
    fields: populatedSchema,
    /**
     * This reducer sets the holdMusicSystemMedia value based on the holdMusicId value
     * The holdMusicSystemMedia field is hidden to the user
     * */
    fieldStateReducer: (state, action) => {
      if (action.type === FormFieldActionTypes.Update && action.payload.name === 'holdMusicId') {
        const holdMusicData = queryClient.getQueryData<{ standardMedia: AudioItem[] }>(
          [
            tenantLocation.phoneTenantId,
            ...queryKeys.audioPicker({
              add: true,
              custom: true,
              standard: true,
              tenantId: tenantLocation.phoneTenantId,
            }),
          ],
          {}
        )?.standardMedia;

        const flattened = holdMusicData ? Object.values(holdMusicData).flat() : [];
        const holdMusicSystemMedia = flattened.some((media) => media.id === action.payload.value);

        return {
          ...state,
          holdMusicSystemMedia: { ...state.holdMusicSystemMedia, value: holdMusicSystemMedia },
        };
      }
      return state;
    },
  });
  const textFieldProps = form.getFieldProps('name');
  const locationOptions = useMemo(() => {
    return (
      tenantLocation.children?.map((location) => ({
        label: location.name ?? '',
        value: location.locationId ?? '',
      })) ?? []
    );
  }, [tenantLocation]);

  const createCallQueue = (state: CallQueueState, formValues: SchemaFormValues) => {
    const finalState = createSubmitPayload({ state, formValues });
    create.mutate(finalState, {
      onSuccess: (data) => {
        alerts.success({
          message: t('Call Queue created successfully.'),
          action: {
            label: t('Go to Call Queue'),
            onClick: () => navigate({ to: `/phone/call-queues/:id`, params: { id: data.callQueueId } }),
          },
        });
        form.reset();
        onSuccess?.(data);
      },
      onError: () => {
        alerts.error('System failed to create Call Queue. Please try again.');
      },
    });
    onClose();
  };

  const cancel = () => {
    form.reset();
    onClose();
  };

  const isUnify = tenantLocation?.locationType === 'unify';

  return (
    <MultiStep.Modal minWidth={800} {...createCallQueueModalProps} onCancel={cancel}>
      {/* Step 1: Call Queue Name */}
      <MultiStep.Step
        id='name'
        header={{ title: t('Create Call Queue') }}
        label={t('Call Queue Name')}
        isValid={textFieldProps.value !== ''}
        footer={{ secondary: t('Cancel'), onClickSecondary: cancel }}
      >
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Heading level={3}>{t('Call Queue Name')}</Heading>
          <div css={{ width: '318px', paddingLeft: theme.spacing(0.25) }}>
            <TextField
              {...textFieldProps}
              label={t('Call Queue Name')}
              name='name'
              data-trackingid={trackingId({
                context: 'setting',
                feature: 'call-queues',
                details: 'create::queue-name-input',
              })}
            />
          </div>
        </div>
      </MultiStep.Step>

      {/* Step 2: Assign Devices */}
      <MultiStep.Step
        css={{ maxWidth: '535px' }}
        id='devices'
        header={{ title: t('Create Call Queue') }}
        label={t('Assign Devices')}
        footer={{
          secondary: t('Cancel'),
          onClickSecondary: cancel,
          helpText:
            callQueue.devices.length > 0
              ? callQueue.devices.length > 1
                ? t('{{count}} devices selected', { count: callQueue.devices.length })
                : t('{{count}} device selected', { count: callQueue.devices.length })
              : undefined,
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            '> div': {
              boxShadow: 'none',
              padding: theme.spacing(0, 0.25),
            },
          }}
        >
          <AssignDeviceStepCard
            setState={setCallQueue}
            selectedDeviceCount={callQueue.devices.length}
            assignedDevices={callQueue.devices}
            tenantLocation={tenantLocation}
          />
        </div>
      </MultiStep.Step>

      {/* Step 3: Routing */}
      <MultiStep.Step
        id='routing'
        header={{ title: t('Create Call Queue') }}
        label={t('Routing')}
        footer={{
          secondary: t('Cancel'),
          onClickSecondary: cancel,
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            '> div': {
              boxShadow: 'none',
              padding: 0,
            },
          }}
        >
          <RoutingCard form={form} condensed />
        </div>
      </MultiStep.Step>

      {/* Step 4: Caller Experience) */}
      <MultiStep.Step
        css={{ maxWidth: '535px' }}
        id='experience'
        header={{ title: t('Create Call Queue') }}
        label={t('Caller Experience')}
        footer={{
          secondary: t('Cancel'),
          onClickSecondary: cancel,
          onClickPrimary: isUnify
            ? () => createCallQueueModalProps.goNextStep()
            : () => createCallQueue(callQueue, form.values),
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            '> div': {
              boxShadow: 'none',
              padding: theme.spacing(0, 0.5),
            },
            maxHeight: `calc(100vh - ${theme.spacing(30)})`,
          }}
        >
          <CallerExperienceCard form={form} tenantId={tenantLocation.phoneTenantId ?? ''} condensed />
        </div>
      </MultiStep.Step>

      {/* Step 5: Location (only if isUnify) */}
      {isUnify && (
        <MultiStep.Step
          id='location'
          header={{ title: t('Create Call Queue') }}
          label={t('Location')}
          footer={{
            secondary: t('Cancel'),
            onClickSecondary: cancel,
            onClickPrimary: () => createCallQueue(callQueue, form.values),
          }}
        >
          <div
            css={{
              '> div': {
                boxShadow: 'none',
                padding: 0,
              },
            }}
          >
            <LocationsCard form={form} options={locationOptions} />
          </div>
        </MultiStep.Step>
      )}
    </MultiStep.Modal>
  );
};
